/** 
  All of the routes for the Material Dashboard 2 React are added here,
  You can add a new route, customize the routes and delete the routes here.
  Once you add a new route on this file it will be visible automatically on
  the Sidenav.
  For adding a new route you can follow the existing routes in the routes array.
  1. The `type` key with the `collapse` value is used for a route.
  2. The `type` key with the `title` value is used for a title inside the Sidenav. 
  3. The `type` key with the `divider` value is used for a divider between Sidenav items.
  4. The `name` key is used for the name of the route on the Sidenav.
  5. The `key` key is used for the key of the route (It will help you with the key prop inside a loop).
  6. The `icon` key is used for the icon of the route on the Sidenav, you have to add a node.
  7. The `collapse` key is used for making a collapsible item on the Sidenav that has other routes
  inside (nested routes), you need to pass the nested routes inside an array as a value for the `collapse` key.
  8. The `route` key is used to store the route location which is used for the react router.
  9. The `href` key is used to store the external links location.
  10. The `title` key is only for the item with the type of `title` and its used for the title text on the Sidenav.
  10. The `component` key is used to store the component of its route.
*/

import { Outlet } from 'react-router-dom'

// @mui icons
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange'
import Icon from '@mui/material/Icon'

// Material Dashboard 2 React layouts
import AddProperty from 'components/addproperty'
import FAQs from 'layouts/FAQs'
import ReorderFAQs from 'layouts/FAQs/components/ReorderFAQs'
import FundingSources from 'layouts/FundingSources'
import Legal from 'layouts/Legal'
import PropertyReports from 'layouts/PropertyReports'
import RecurringRevenue from 'layouts/RecurringRevenue'
import RevenueReports from 'layouts/RevenueReports'
import SellProperty from 'layouts/SellProperty'
import TransactionsManagement from 'layouts/TransactionsManagement'
import UserDetails from 'layouts/UserDetails'
import UserManagement from 'layouts/UserManagement'
import UserReports from 'layouts/UserReports'
import Amenities from 'layouts/amenities'
import VerifyOTPPage from 'layouts/authentication/otp'
import Login from 'layouts/authentication/sign-in'
import SignUp from 'layouts/authentication/sign-up'
import Dashboard from 'layouts/dashboard'
import Dashboard2 from 'layouts/dashboard2'
import Email from 'layouts/email/index'
import NotFound from 'layouts/errors/not-found'
import WentWrong from 'layouts/errors/went-wrong'
import Finance from 'layouts/finance'
import Notifications from 'layouts/notifications'
import Profile from 'layouts/profile'
import ManagePropertyType from 'layouts/property-type'
import PropertyManagement from 'layouts/propertyManagement'
import Sms from 'layouts/sms/index'

const routes = [
  {
    type: 'collapse',
    name: 'Dashboard',
    key: 'dashboard',
    icon: <Icon fontSize='small'>dashboard</Icon>,
    path: '/',
    component: Dashboard,
    displayInSidebar: true,
    isAuthRequired: true,
  },
  // === This is only kept for Reference. Will be removed after dashboard design is completed ===
  {
    type: 'collapse',
    name: 'Dashboard2',
    key: 'dashboard2',
    icon: <Icon fontSize='small'>dashboard</Icon>,
    path: '/dashboard2',
    component: Dashboard2,
    displayInSidebar: false,
    isAuthRequired: true,
  },
  // === ===
  // {
  //   type: 'collapse',
  //   name: 'Tables',
  //   key: 'tables',
  //   icon: <Icon fontSize="small">table_view</Icon>,
  //   path: '/tables',
  //   component: Tables,
  //   displayInSidebar: false,
  //   isAuthRequired: true,
  // },
  // {
  //   type: 'collapse',
  //   name: 'Billing',
  //   key: 'billing',
  //   icon: <Icon fontSize="small">receipt_long</Icon>,
  //   path: '/billing',
  //   component: Billing,
  //   displayInSidebar: false,
  //   isAuthRequired: true,
  // },
  {
    type: 'collapse',
    name: 'Notifications',
    key: 'notifications',
    icon: <Icon fontSize='small'>notifications</Icon>,
    path: '/notifications',
    component: Notifications,
    displayInSidebar: false,
    isAuthRequired: true,
  },
  {
    type: 'collapse',
    name: 'Profile',
    key: 'profile',
    icon: <Icon fontSize='small'>person</Icon>,
    path: '/profile',
    component: Profile,
    displayInSidebar: false,
    isAuthRequired: true,
  },
  {
    type: 'collapse',
    name: 'Sign In',
    key: 'sign-in',
    icon: <Icon fontSize='small'>login</Icon>,
    path: '/login',
    component: Login,
    displayInSidebar: false,
    isAuthRequired: false,
  },
  {
    type: 'collapse',
    name: '2FA',
    key: 'verify-otp',
    path: '/verify-otp',
    component: VerifyOTPPage,
    displayInSidebar: false,
    isAuthRequired: true,
    isProtected: true,
  },
  {
    type: 'collapse',
    name: 'Sign Up',
    key: 'sign-up',
    icon: <Icon fontSize='small'>assignment</Icon>,
    path: '/sign-up',
    component: SignUp,
    displayInSidebar: false,
    isAuthRequired: false,
  },
  {
    type: 'collapse',
    name: 'Not Found',
    key: 'not-found',
    icon: <Icon fontSize='small'>login</Icon>,
    path: '/not-found',
    component: NotFound,
    displayInSidebar: false,
    isAuthRequired: false,
  },
  {
    type: 'collapse',
    name: 'Went Wrong',
    key: 'went-wrong',
    icon: <Icon fontSize='small'>login</Icon>,
    path: '/went-wrong',
    component: WentWrong,
    displayInSidebar: false,
    isAuthRequired: false,
  },
  {
    type: 'collapse',
    name: 'Add Property',
    key: 'addProperty',
    icon: <Icon fontSize='small'>add_home_work</Icon>,
    path: '/properties/addProperty',
    component: AddProperty,
    displayInSidebar: false,
    isAuthRequired: true,
  },
  {
    type: 'collapse',
    title: 'Property Management',
    name: 'Properties',
    key: 'property-management',
    icon: <Icon fontSize='small'>house</Icon>,
    path: '/',
    component: () => <Outlet />,
    dropdown: true,
    displayInSidebar: true,
    isAuthRequired: true,
    subRoutes: [
      {
        type: 'collapse',
        name: 'Properties',
        key: 'properties',
        icon: <Icon fontSize='small'>house</Icon>,
        path: '/properties',
        component: PropertyManagement,
        displayInSidebar: true,
        isAuthRequired: true,
      },
      {
        type: 'collapse',
        name: 'Manage Property Type',
        key: 'property-type',
        icon: <Icon fontSize='small'>assignment</Icon>,
        path: '/property-type',
        component: ManagePropertyType,
        displayInSidebar: true,
        isAuthRequired: true,
      },
      {
        type: 'collapse',
        name: 'Amenities',
        key: 'amenities',
        icon: <Icon fontSize='small'>garage</Icon>,
        path: '/amenities',
        component: Amenities,
        displayInSidebar: true,
        isAuthRequired: true,
      },
    ],
  },
  {
    type: 'collapse',
    title: 'Users',
    name: 'users',
    key: 'users',
    icon: <AccountCircleIcon />,
    path: '/',
    component: () => <Outlet />,
    dropdown: true,
    displayInSidebar: true,
    isAuthRequired: true,
    subRoutes: [
      {
        type: 'collapse',
        name: 'User Management',
        key: 'users',
        icon: <Icon fontSize='small'>manage_accounts</Icon>,
        path: '/users',
        component: UserManagement,
        displayInSidebar: true,
        isAuthRequired: true,
      },
      {
        type: 'collapse',
        name: 'Compliance Management',
        key: 'funding-sources',
        icon: <CurrencyExchangeIcon />,
        path: '/funding-sources',
        component: FundingSources,
        displayInSidebar: true,
        isAuthRequired: true,
      },
    ],
  },
  {
    type: 'collapse',
    title: 'Reports and Analysis',
    name: 'Reports and Analysis',
    key: 'reports-analysis',
    icon: <Icon fontSize='small'>assessment</Icon>,
    path: '/',
    component: () => <Outlet />,
    dropdown: true,
    displayInSidebar: true,
    isAuthRequired: true,
    subRoutes: [
      {
        type: 'collapse',
        name: 'Revenue',
        key: 'revenue',
        icon: <Icon fontSize='small'>pie_chart</Icon>,
        path: 'revenue',
        component: RevenueReports,
        displayInSidebar: true,
        isAuthRequired: true,
      },
      {
        type: 'collapse',
        name: 'Recurring Revenue',
        key: 'recurring-revenue',
        icon: <Icon fontSize='small'>pie_chart</Icon>,
        path: 'recurring-revenue',
        component: RecurringRevenue,
        displayInSidebar: true,
        isAuthRequired: true,
      },
      {
        type: 'collapse',
        name: 'User Reports',
        key: 'user-reports',
        icon: <Icon fontSize='small'>pie_chart</Icon>,
        path: 'user-reports',
        component: UserReports,
        displayInSidebar: true,
        isAuthRequired: true,
      },
      {
        type: 'collapse',
        name: 'Property Reports',
        key: 'property-reports',
        icon: <Icon fontSize='small'>pie_chart</Icon>,
        path: 'property-reports',
        component: PropertyReports,
        displayInSidebar: true,
        isAuthRequired: true,
      },
    ],
  },
  {
    type: 'collapse',
    name: 'Transactions',
    key: 'transactions',
    icon: <Icon fontSize='small'>paid</Icon>,
    path: '/transactions',
    component: TransactionsManagement,
    displayInSidebar: true,
    isAuthRequired: true,
  },
  {
    type: 'collapse',
    name: 'User Details',
    key: 'userdetails',
    icon: <Icon fontSize='small'>person</Icon>,
    path: '/users/:userId',
    component: UserDetails,
    displayInSidebar: false,
    isAuthRequired: true,
  },
  {
    type: 'collapse',
    name: 'Finance',
    key: 'finance',
    icon: <Icon fontSize='small'>paid</Icon>,
    path: '/finance',
    component: Finance,
    displayInSidebar: true,
    isAuthRequired: true,
  },
  {
    type: 'collapse',
    title: 'Legal and Help Center',
    name: 'Legal and Help Center',
    key: 'legal-faq',
    icon: <Icon fontSize='small'>policy</Icon>,
    path: '/',
    component: () => <Outlet />,
    dropdown: true,
    displayInSidebar: true,
    isAuthRequired: true,
    subRoutes: [
      {
        type: 'collapse',
        name: 'Legal',
        key: 'legal',
        icon: <Icon fontSize='small'>description</Icon>,
        path: 'legal',
        component: Legal,
        displayInSidebar: true,
        isAuthRequired: true,
      },
      {
        type: 'collapse',
        name: 'FAQ',
        key: 'faq',
        icon: <Icon fontSize='small'>live_help</Icon>,
        path: 'faq',
        component: FAQs,
        displayInSidebar: true,
        isAuthRequired: true,
      },
      {
        type: 'collapse',
        name: 'FAQ Reorder',
        key: 'faq-reorder',
        icon: <Icon fontSize='small'>live_help</Icon>,
        path: 'faq/reorder',
        component: ReorderFAQs,
        displayInSidebar: false,
        isAuthRequired: true,
      },
    ],
  },
  {
    type: 'collapse',
    title: 'Email and Messages',
    name: 'Email and Messages',
    key: 'email-message',
    icon: <Icon fontSize='small'>outbox</Icon>,
    path: '/',
    component: () => <Outlet />,
    dropdown: true,
    displayInSidebar: true,
    isAuthRequired: true,
    subRoutes: [
      {
        type: 'collapse',
        name: 'Email',
        key: 'email',
        icon: <Icon fontSize='small'>email</Icon>,
        path: 'email',
        component: Email,
        displayInSidebar: true,
        isAuthRequired: true,
      },
      {
        type: 'collapse',
        name: 'Messages',
        key: 'messages',
        icon: <Icon fontSize='small'>textsms</Icon>,
        path: 'messages',
        component: Sms,
        displayInSidebar: true,
        isAuthRequired: true,
      },
    ],
  },
  {
    type: 'collapse',
    name: 'Update Property',
    key: 'update-property',
    icon: <Icon fontSize='small'>pie_chart</Icon>,
    path: '/properties/:propertyId',
    component: AddProperty,
    displayInSidebar: false,
    isAuthRequired: true,
  },
  {
    type: 'collapse',
    name: 'Sell Property',
    key: 'sell',
    icon: <Icon fontSize='small'>sell</Icon>,
    path: '/sell-properties',
    component: SellProperty,
    displayInSidebar: true,
    isAuthRequired: true,
  },
]

export default routes
